// Transaction edit modal — edit amount / currency / category / description / date / type / account.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAccounts } from "@/lib/useAccounts";

const TYPES = ["expense", "income", "revenue", "recurring", "borrow", "lend", "savings"];

export function TxEditModal({ open, tx, cats, onClose, onSaved }) {
  const { t, lang } = useI18n();
  const { items: accounts = [] } = useAccounts();
  const [f, setF] = useState({});
  const [ccys, setCcys] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/currencies").then((r) => setCcys(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!open || !tx) return;
    setF({ type: tx.type, amount: tx.original_amount, currency: tx.original_currency,
      category: tx.category, description: tx.description || "", date: tx.date,
      account_id: tx.account_id || "", to_account_id: tx.to_account_id || "" });
  }, [open, tx]);
  if (!open || !tx) return null;
  const AR = lang === "ar";
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!(f.amount > 0)) { toast.error(AR ? "المبلغ غير صحيح" : "Invalid amount"); return; }
    setBusy(true);
    try { await api.patch(`/transactions/${tx.id}`, f); toast.success(t("saved")); onSaved?.(); onClose(); }
    catch { toast.error(t("error")); } finally { setBusy(false); }
  };
  return (
    <div data-testid="tx-edit-modal" className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-md grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-md neu rounded-3xl p-5 space-y-3 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-display font-extrabold text-lg neu-accent-blue">{AR ? "تعديل المعاملة" : "Edit transaction"}</p>
          <button data-testid="tx-edit-close" onClick={onClose} className="p-1.5 rounded-lg text-slate-500"><X className="w-5 h-5" /></button>
        </div>
        <select data-testid="tx-edit-type" value={f.type || ""} onChange={(e) => set("type", e.target.value)} className="w-full px-3 py-2.5 rounded-2xl neu-inset text-sm">
          {TYPES.map((k) => <option key={k} value={k}>{t(k) || k}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="tx-edit-amount" type="number" inputMode="decimal" step="0.01" value={f.amount ?? ""} onChange={(e) => set("amount", parseFloat(e.target.value))} placeholder={t("amount")} className="w-full px-3 py-2.5 rounded-2xl neu-inset text-sm tabular-nums" />
          <select data-testid="tx-edit-currency" value={f.currency || "AED"} onChange={(e) => set("currency", e.target.value)} className="w-full px-3 py-2.5 rounded-2xl neu-inset text-sm">
            {ccys.map((c) => <option key={c.code || c} value={c.code || c}>{c.code || c}</option>)}
          </select>
        </div>
        <select data-testid="tx-edit-category" value={f.category || ""} onChange={(e) => set("category", e.target.value)} className="w-full px-3 py-2.5 rounded-2xl neu-inset text-sm">
          {cats.map((c) => <option key={c.id} value={c.id}>{c[lang] || c.en}</option>)}
        </select>
        <input data-testid="tx-edit-description" value={f.description || ""} onChange={(e) => set("description", e.target.value)} placeholder={AR ? "الوصف" : "Description"} className="w-full px-3 py-2.5 rounded-2xl neu-inset text-sm" />
        <input data-testid="tx-edit-date" type="date" dir="ltr" value={f.date || ""} onChange={(e) => set("date", e.target.value)} className="w-full px-3 py-2.5 rounded-2xl neu-inset text-sm tabular-nums" />
        <select data-testid="tx-edit-account" value={f.account_id || ""} onChange={(e) => set("account_id", e.target.value)} className="w-full px-3 py-2.5 rounded-2xl neu-inset text-sm">
          <option value="">{AR ? "— بدون حساب" : "— No account"}</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.currency}</option>)}
        </select>
        <button data-testid="tx-edit-save" disabled={busy} onClick={save} className="w-full py-3 rounded-2xl neu-fill-blue text-white font-semibold flex items-center justify-center gap-2 shadow-lg disabled:opacity-60">
          <Save className="w-4 h-4" /> {busy ? "…" : (AR ? "حفظ التغييرات" : "Save changes")}
        </button>
      </div>
    </div>
  );
}
